/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BlueprintRoutes, TBlueprintRoutesSchema } from "@adapptio/lib-core";
import {
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	serializeIDTToYAML,
	TBlueprintIDTNode,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import { RuntimeService } from "../../../../runtime/RuntimeService";
import {
	createEventEmitter,
	emitEvent,
	onEvent,
	removeAllEventListeners,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { debounce } from "@adapptio/lib-editor-ui";

export class RoutesModel {
	private dCtx: DesignContext;
	private model: TGetBlueprintSchemaModel<TBlueprintRoutesSchema>;

	private lastSnapshot: TBlueprintIDTNode;

	public onModelChange = createEventEmitter();
	public onHistoryChange = createEventEmitter();
	public undoRedoManager = new UndoRedoManager({ historyLimit: 10 });

	constructor(private runtimeSvc: RuntimeService) {
		const runtimeResolvers = runtimeSvc.getBaseResolvers();

		this.dCtx = new DesignContext({
			resolvers: runtimeResolvers,
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});

		onEvent(this.dCtx.modelChangeEvent, () => {
			this.emitModelChange();
			this.createSnapshot();
		});
	}

	private async initModel(): Promise<void> {
		this.createSnapshot();
		this.emitModelChange();
	}

	private emitModelChange() {
		emitEvent(this.onModelChange);
	}

	public async loadDocument(blueprintContent: string): Promise<void> {
		this.model = this.dCtx.loadYAMLDocument(BlueprintRoutes, blueprintContent, "local://routes");
		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	public getModel(): TGetBlueprintSchemaModel<TBlueprintRoutesSchema> {
		return this.model;
	}

	public getItems(): TGetBlueprintSchemaModel<TBlueprintRoutesSchema>["props"]["spec"]["items"] {
		return this.model?.props?.spec?.items || [];
	}

	public createDefaultItemValue() {
		return this.model?.props?.spec?.schema?.opts?.value?.createDefault(this.dCtx, this.model.props.spec);
	}

	public addNewItem(key: string, { path, label }: { path: string; label: string }): void {
		const defaultValue = this.createDefaultItemValue();

		defaultValue.props.path.schema.setValue(defaultValue.props.path, path);
		defaultValue.props.label.schema.setValue(defaultValue.props.label, label);

		this.model.props.spec.schema.addElementModel(this.model.props.spec, key, defaultValue, true);
	}

	public removeItem(
		key: TGetBlueprintSchemaModel<TBlueprintRoutesSchema>["props"]["spec"]["items"][number]["key"]
	): void {
		const index = this?.model?.props?.spec?.items?.findIndex((item) => item?.key?.nodeId === key?.nodeId);

		if (index < 0) {
			return;
		}

		this.model?.props?.spec?.schema?.removeElement(this.model.props.spec, index, true);
	}

	private undoRedoHandle(snapshot: TBlueprintIDTNode) {
		this.model = BlueprintRoutes.parse(this.dCtx, snapshot);
		this.lastSnapshot = snapshot;
		this.emitHistoryChange();
		this.emitModelChange();
	}

	private createSnapshot = debounce(() => {
		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.model.schema.serialize(this.model, [ "$" ]);
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.undoRedoHandle(prevSnapshot),
				redo: () => this.undoRedoHandle(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});

			this.emitHistoryChange();
		}
	}, 500);

	private emitHistoryChange(): void {
		emitEvent(this.onHistoryChange);
	}

	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.model.schema.serialize(this.model, [ "$" ]);
		return serializeIDTToYAML(idt);
	}

	public dispose(): void {
		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		this.dCtx.destroy();

		removeAllEventListeners(this.onModelChange);

		this.model = null;
		this.dCtx = null;
	}
}
